import { getAnalyticsConfig } from '@ynap/config-state'
import { getCountries } from '@ynap/country-state'
import { getLocale, getConfig, getCurrentOrderShippingInfo, getCurrentShippingOption } from '@ynap/cart-state'
import { getCountryNameByCode, getStateNameByCode } from '@ynap/locale-utils'
import getCart, { getUserStatus } from './getCart'
import { isFullyShippingRestricted } from './getShippingRestrictions'
import { fromState, createCombinedSelector } from '../utils/selectorUtils'
import { getCurrentPackagingOption } from './getPackagingOptionsStep'
import { Address, Messages, OrderItem, ShoppingCart } from 'app/types/common-types'
import { Countries, StoreState } from 'app/state/types'
import { FetchCartError } from '../reducers/fetchCartError'
import { IsShippingValid } from '../reducers/isShippingAddressValid'

export default createCombinedSelector({
    name: 'getShippingAddressStep',
    state: ['locale', 'config', 'countries', 'cart', 'deviceType', 'isShippingAddressValid', 'crisisMessages', 'jitMessages', 'isFetchingProvinces'],
    props: ['messages'],
    passThroughState: [{ loading: fromState('isFetchingCart') }, { error: fromState('fetchCartError') }],
    fn: getShippingAddressStepInternal,
})

interface State extends StoreState {
    isFetchingCart: boolean
    fetchCartError: FetchCartError
    isShippingAddressValid: IsShippingValid
    isFetchingProvinces: boolean
}

type Props = {
    messages: Messages
}

type SavedAddress = Address & {
    countryName: string
    stateName: string
}

export function getShippingAddressStepInternal(state: State, props: Props) {
    const { isFetchingCart: loading, fetchCartError: error, isShippingAddressValid, crisisMessages, jitMessages, isFetchingProvinces } = state
    const { country } = getLocale(state)
    const config = getConfig(state)
    const addresses = getShippingAddressesByCountry(state, country)
    const currentAddress = getCurrentShippingAddress(state)
    const { firstName, lastName, addressLine, transient } = currentAddress || {}
    const shouldAddTransient = transient && firstName !== '' && lastName !== '' && addressLine?.[0] !== ''
    const userStatus = getUserStatus(state)
    const savedAddresses = shouldAddTransient && userStatus === 'R' && addresses ? [currentAddress, ...addresses] : addresses

    // load shopping bag and check for restrictions and whether a shipping address has been selected
    const cart = getCart(state, props)
    const {
        lineItems,
        totals,
        genericTotal,
        orderItem = [],
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics,
        hasJITItems,
        isGift,
        hasShippingMethodChanged,
        isPostSplit,
    } = cart
    const shippingRestricted = isFullyShippingRestricted(orderItem)
    const hasOrderItemAddress = !!orderItem[0]?.addressLine?.[0]
    const [currentShippingOption, currentShippingOptionJit] = getCurrentShippingOption(state, props)
    const [currentOrderShippingInfo, currentOrderShippingInfoJit] = getCurrentOrderShippingInfo(state)

    // for brands that do not use Fast Checkout, this may be undefined, which should be treated as true
    const isSelectedAddressValid = !(isShippingAddressValid === false)
    // step is complete if a valid address has been selected and no items are restricted from shipping to it
    const completed = addresses ? hasOrderItemAddress && isSelectedAddressValid && !shippingRestricted : undefined

    let { countries = {} } = getCountries(state)
    if (Object.keys(countries).length > 0) {
        countries = {
            [country]: countries[country],
        }
    }

    return {
        addresses: savedAddresses,
        config,
        currentAddress,
        lineItems,
        totals,
        loading,
        error,
        completed,
        analytics: getAnalyticsConfig(state),
        genericTotal,
        shippingRestricted,
        countries,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics,
        isSelectedAddressValid,
        currentShippingOption,
        currentShippingOptionJit,
        currentOrderShippingInfo,
        currentOrderShippingInfoJit,
        crisisMessages,
        hasJITItems,
        currentPackagingOption: getCurrentPackagingOption(state, props),
        basicOnly: config.head?.flags?.basicPackagingOnly,
        isGift,
        jitMessages,
        hasShippingMethodChanged,
        isPostSplit,
        isFetchingProvinces,
        userStatus,
        hasSelectedAddress: hasOrderItemAddress,
    }
}

export function getSavedAddresses(state: State): SavedAddress[] | undefined {
    const { cart = {} } = state
    const { contact } = cart as Pick<ShoppingCart, 'contact'>
    if (contact) {
        // Only include if no self address
        const savedAddresses = contact.filter((address) => address.xcont_isSelfAddress !== 'true')

        return savedAddresses.map((address) => {
            return {
                ...address,
                state: String(address.state || ''),
                city: String(address.city || ''),
                PCCC: address.PCCC,
                countryName: getCountryNameByCode(address.country, getCountries(state)),
                stateName: getStateNameByCode(address.state, address.country, getCountries(state)),
                transient: false,
            }
        })
    }
}

export function getShippingAddresses(state: State, includeTransient = false) {
    const { cart = {} } = state
    const { orderItem } = cart as Pick<ShoppingCart, 'orderItem'>

    if (orderItem) {
        // Get addresses from order items (includes transient addresses which won't be in address book)
        const usableShippingAddresses = orderItem.reduce((accumulator, item) => {
            // If an order item has an addressId and is no self address it's a valid shipping address
            if (item.addressId && item.x_isSelfAddress !== 'true' && (includeTransient || !item.transient)) {
                accumulator[`${item.addressId}`] = mapAddressFields(item, state)
            }
            return accumulator
        }, {})

        // Find additional addresses in user's address book
        const savedAddresses = getSavedAddresses(state)
        if (savedAddresses) {
            savedAddresses.reduce((accumulator, address) => {
                // Only include if listed in usable shipping addresses
                const isUsableShippingAddress = orderItem.find(({ usableShippingAddress = [] }) =>
                    usableShippingAddress.find(({ addressId }) => addressId === address.addressId)
                )
                if (isUsableShippingAddress) {
                    accumulator[`${address.addressId}`] = address
                }
                return accumulator
            }, usableShippingAddresses)
        }

        return values(usableShippingAddresses)
    }
}

export function getShippingAddressesByCountry(state: State, country: Countries) {
    const shippingAddresses = getShippingAddresses(state)
    if (shippingAddresses) {
        return shippingAddresses.filter((address) => address.country === country)
    }
}

export function getCurrentShippingAddress(state: State) {
    const shippingAddresses = getShippingAddresses(state, true)

    if (shippingAddresses) {
        const [currentShippingAddress] = shippingAddresses

        // APPLE PAY error
        // When using apps if users enter apple pay flow, some transient address fields are set to "Undefined"
        // Remove the Undefined values so these dont show up in edit address fields
        const { firstName, lastName, addressLine } = currentShippingAddress || {}
        const transform = currentShippingAddress && {
            ...currentShippingAddress,
            firstName: firstName === 'Undefined' ? '' : firstName,
            lastName: lastName === 'Undefined' ? '' : lastName,
            addressLine: addressLine?.[0] === 'Undefined' ? ['', '', ''] : addressLine,
        }

        return transform
    }
}

export function mapAddressFields(orderItem: OrderItem, reduxState: State) {
    const {
        addressId,
        displayNickName,
        addressType,
        nickName,
        personTitle,
        firstName,
        middleName,
        lastName,
        addressLine,
        city,
        zipCode,
        state,
        stateOrProvinceName,
        country,
        PCCC,
        pronunciations,
        phone1,
        phone2,
        email1,
        email2,
        transient,
    } = orderItem

    return {
        addressId,
        displayNickName,
        addressType,
        nickName,
        personTitle,
        firstName,
        middleName,
        lastName,
        addressLine,
        city,
        zipCode,
        state,
        stateOrProvinceName,
        country,
        pronunciations,
        phone1,
        phone2,
        email1,
        email2,
        transient,
        PCCC,
        countryName: getCountryNameByCode(country, getCountries(reduxState)),
        stateName: getStateNameByCode(state, country, getCountries(reduxState)),
    }
}

// TODO: Once address fields response issue is addressed(no pun intended) we can remove
// https://jira.nap/browse/SPEOISNRS-2254
const removeEmptyStrings = (obj) => {
    let newObj = {}
    Object.keys(obj).forEach((prop) => {
        if (obj[prop] !== '') {
            newObj[prop] = obj[prop]
        }
    })
    return newObj
}

// Object.values polyfill
function values(obj) {
    return Object.keys(obj).map((key) => removeEmptyStrings(obj[key]))
}
